'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import type { SignupUserType } from '@/lib/auth/signup-user-type';
import type { CustomerSegment } from '@/types';
import { PRODUCT_TERMS_VERSION, TERMS_ACCEPTANCE_REQUIRED_MESSAGE } from '@/lib/marketing/consent-copy';

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  sendWhatsAppOtp: (phone: string, termsAccepted: boolean, turnstileToken?: string) => Promise<{ error: { message: string } | null }>;
  verifyWhatsAppOtp: (phone: string, otp: string, userType?: SignupUserType, customerSegment?: CustomerSegment, termsAccepted?: boolean) => Promise<{ error: { message: string } | null }>;
  signInWithGoogle: (redirectTo?: string, userType?: SignupUserType, customerSegment?: CustomerSegment, termsAccepted?: boolean) => Promise<{ error: { message: string } | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const localSearchE2EAuthEnabled = process.env.NODE_ENV !== 'production'
    && process.env.NEXT_PUBLIC_SEARCH_V2_E2E_AUTH === 'true';
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const supabase = createClient();

  // Initialize auth state
  useEffect(() => {
    if (
      localSearchE2EAuthEnabled
      && window.localStorage.getItem('search-v2-e2e-auth') === '1'
    ) {
      queueMicrotask(() => setState({
          user: { id: 'local-search-e2e-user' } as User,
          session: null,
          isLoading: false,
          isAuthenticated: true,
        }));
      return;
    }
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        setState({
          user: session?.user ?? null,
          session: session,
          isLoading: false,
          isAuthenticated: !!session?.user,
        });
      } catch (error) {
        console.error('Error initializing auth:', error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initializeAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setState({
          user: session?.user ?? null,
          session: session,
          isLoading: false,
          isAuthenticated: !!session?.user,
        });

        // Handle sign out redirect
        if (event === 'SIGNED_OUT') {
          // Let the component handle redirect
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [localSearchE2EAuthEnabled, supabase.auth]);

  // Send OTP via WhatsApp
  const sendWhatsAppOtp = useCallback(async (phone: string, termsAccepted: boolean, turnstileToken?: string): Promise<{ error: { message: string } | null }> => {
    try {
      const body: Record<string, string | boolean> = {
        phone,
        terms_accepted: termsAccepted === true,
      };
      if (turnstileToken) body.turnstileToken = turnstileToken;

      const response = await fetch('/api/auth/send-whatsapp-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          error: { message: result.error || 'Failed to send OTP' },
        };
      }

      return { error: null };
    } catch (error) {
      console.error('Send WhatsApp OTP error:', error);
      return { error: { message: 'Network error. Please check your connection.' } };
    }
  }, []);

  // Verify WhatsApp OTP and establish session
  const verifyWhatsAppOtp = useCallback(async (phone: string, otp: string, userType?: SignupUserType, customerSegment?: CustomerSegment, termsAccepted?: boolean): Promise<{ error: { message: string } | null }> => {
    try {
      // Step 1: Verify OTP with our server (returns token_hash)
      const { getStoredAttribution } = await import('@/lib/attribution');
      const attribution = getStoredAttribution();
      const response = await fetch('/api/auth/verify-whatsapp-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          otp,
          attribution,
          user_type: userType,
          customer_segment: customerSegment,
          terms_accepted: termsAccepted === true,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          error: { message: result.error || 'Invalid OTP' },
        };
      }

      // Step 2: Exchange token_hash for a Supabase session
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: result.token_hash,
        type: 'magiclink',
      });

      if (verifyError) {
        console.error('Session exchange error:', verifyError);
        return {
          error: { message: verifyError.message || 'Failed to create session. Please try again.' },
        };
      }

      return { error: null };
    } catch (error) {
      console.error('Verify WhatsApp OTP error:', error);
      return { error: { message: 'Network error. Please check your connection.' } };
    }
  }, [supabase.auth]);

  // Sign in with Google (OAuth). Kicks off redirect flow; caller won't get a
  // result here — Supabase redirects the browser to Google, then back to
  // /auth/callback which finishes the exchange.
  const signInWithGoogle = useCallback(async (redirectTo?: string, userType?: SignupUserType, customerSegment?: CustomerSegment, termsAccepted?: boolean): Promise<{ error: { message: string } | null }> => {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const callback = new URL('/auth/callback', origin);
      if (redirectTo) callback.searchParams.set('redirect', redirectTo);
      if (userType) callback.searchParams.set('user_type', userType);
      if (customerSegment) callback.searchParams.set('customer_segment', customerSegment);
      if (termsAccepted === true) {
        const intentResponse = await fetch('/api/auth/google-terms-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            terms_accepted: true,
            terms_version: PRODUCT_TERMS_VERSION,
          }),
        });
        if (!intentResponse.ok) {
          const intentResult = await intentResponse.json().catch(() => null) as { error?: string } | null;
          return { error: { message: intentResult?.error || TERMS_ACCEPTANCE_REQUIRED_MESSAGE } };
        }
        callback.searchParams.set('terms_accepted', 'true');
        callback.searchParams.set('terms_version', PRODUCT_TERMS_VERSION);
      } else if (typeof termsAccepted === 'boolean') {
        return { error: { message: TERMS_ACCEPTANCE_REQUIRED_MESSAGE } };
      }

      // Persist attribution into callback query so the server route can stamp it
      // (localStorage is not accessible from the OAuth redirect back).
      try {
        const { getStoredAttribution } = await import('@/lib/attribution');
        const attr = getStoredAttribution();
        if (attr) {
          callback.searchParams.set('attr', encodeURIComponent(JSON.stringify(attr)));
        }
      } catch {
        // Attribution is best-effort
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callback.toString(),
          queryParams: { access_type: 'offline', prompt: 'select_account' },
        },
      });

      if (error) return { error: { message: error.message || 'Failed to sign in with Google' } };
      return { error: null };
    } catch (error) {
      console.error('Google sign-in error:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }, [supabase.auth]);

  // Sign out
  //
  // Hits the server route first so every auth cookie chunk is cleared via the
  // response (browser-only signOut sometimes leaves server-scoped chunks behind,
  // which made middleware bounce users right back to /dashboard). Then clears
  // the client-side session so React state updates.
  const signOut = useCallback(async (): Promise<{ error: AuthError | null }> => {
    try {
      await fetch('/api/auth/signout', { method: 'POST' });
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error: error as AuthError };
    }
  }, [supabase.auth]);

  // Refresh session
  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      const { data: { session } } = await supabase.auth.refreshSession();
      setState(prev => ({
        ...prev,
        user: session?.user ?? null,
        session: session,
        isAuthenticated: !!session?.user,
      }));
    } catch (error) {
      console.error('Refresh session error:', error);
    }
  }, [supabase.auth]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        sendWhatsAppOtp,
        verifyWhatsAppOtp,
        signInWithGoogle,
        signOut,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
